import React from 'react';
import { Box, Container, Typography, Paper } from '@mui/material';
import { Link } from 'react-router-dom';

const TermsOfServicePage: React.FC = () => {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        padding: '2rem 0',
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={3}
          sx={{
            padding: '3rem',
            backgroundColor: 'white',
            borderRadius: '8px',
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontSize: '2rem',
              fontWeight: 600,
              color: '#333',
              marginBottom: '2rem',
              textAlign: 'center',
            }}
          >
            Terms of Service
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: '#666',
              marginBottom: '2rem',
              fontStyle: 'italic',
            }}
          >
            Last updated: {new Date().toLocaleDateString()}
          </Typography>

          <Box sx={{ marginBottom: '2rem' }}>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                fontSize: '1rem',
                color: '#555',
                textAlign: 'justify',
                marginBottom: '1rem',
              }}
            >
              By using this website and uploading audio files, you acknowledge and agree that all uploaded content is the sole responsibility of the user who submitted it. The site owner and operators of this service do not claim ownership of, review, or verify the legality, accuracy, or integrity of any uploaded files. We are not liable for any copyright infringement, illegal content, or other legal issues arising from the files users choose to upload. You are solely responsible for ensuring that your use of this service complies with all applicable laws and regulations.
            </Typography>
          </Box>

          <Box sx={{ marginBottom: '2rem' }}>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#2c5aa0',
                marginBottom: '1rem',
              }}
            >
              User Responsibilities
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginBottom: '1rem',
                textAlign: 'justify',
              }}
            >
              You agree to:
            </Typography>
            <Typography
              variant="body1"
              component="ul"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginLeft: '2rem',
                marginBottom: '1rem',
              }}
            >
              <li>Only upload audio files for which you have the legal right to use and process</li>
              <li>Not upload any content that violates copyright, trademark, or other intellectual property rights</li>
              <li>Not upload any illegal, harmful, or offensive content</li>
              <li>Maintain the security of your account credentials</li>
              <li>Use the service in compliance with all applicable laws and regulations</li>
            </Typography>
          </Box>

          <Box sx={{ marginBottom: '2rem' }}>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#2c5aa0',
                marginBottom: '1rem',
              }}
            >
              Google Drive Integration
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginBottom: '1rem',
                textAlign: 'justify',
              }}
            >
              Our service offers optional integration with Google Drive to allow you to use audio files from your Google Drive account. By using this feature, you acknowledge and agree to the following:
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginBottom: '1rem',
                textAlign: 'justify',
                fontWeight: 600,
              }}
            >
              Authorization and Consent:
            </Typography>
            <Typography
              variant="body1"
              component="ul"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginLeft: '2rem',
                marginBottom: '1rem',
              }}
            >
              <li>You understand that authorizing Google Drive access grants our application access using the <code>drive.file</code> scope, which allows access only to files that our application creates or that you explicitly open or select through the Google Drive file picker</li>
              <li>You consent to our application accessing and downloading only the files that you explicitly select through the Google Drive file picker interface (or that the app creates)</li>
              <li>You acknowledge that we do not access your entire Google Drive—only files you choose via the picker or that the app creates</li>
              <li>You understand that you can revoke this access at any time through your <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" style={{ color: '#2c5aa0' }}>Google Account permissions settings</a></li>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginBottom: '1rem',
                textAlign: 'justify',
                fontWeight: 600,
              }}
            >
              Your Responsibilities:
            </Typography>
            <Typography
              variant="body1"
              component="ul"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginLeft: '2rem',
                marginBottom: '1rem',
              }}
            >
              <li>You are solely responsible for ensuring that any files you select from Google Drive are files for which you have the legal right to use, process, and download</li>
              <li>You agree not to select files that violate copyright, trademark, or other intellectual property rights</li>
              <li>You agree not to select files containing illegal, harmful, or offensive content</li>
              <li>You understand that by selecting a file from Google Drive, you are authorizing us to download and process that specific file</li>
              <li>You are responsible for maintaining the security of your Google account and for any actions taken through your authorized account</li>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginBottom: '1rem',
                textAlign: 'justify',
                fontWeight: 600,
              }}
            >
              Limitations and Restrictions:
            </Typography>
            <Typography
              variant="body1"
              component="ul"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginLeft: '2rem',
                marginBottom: '1rem',
              }}
            >
              <li>Our application will only access files that you explicitly select through the Google Drive file picker</li>
              <li>We do not modify, delete, create, or change permissions on any files in your Google Drive</li>
              <li>We do not browse, list, or access files without your explicit selection</li>
              <li>Access tokens are stored locally in your browser and expire automatically</li>
              <li>We are not responsible for any issues arising from your use of Google Drive files, including but not limited to file access permissions, file availability, or Google Drive service interruptions</li>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginBottom: '1rem',
                textAlign: 'justify',
              }}
            >
              <strong>Important:</strong> By using the Google Drive integration feature, you represent and warrant that you have the legal right to authorize access to and use of any files you select from your Google Drive. You are solely responsible for any legal consequences arising from your use of files from Google Drive through our service.
            </Typography>
          </Box>

          <Box sx={{ marginBottom: '2rem' }}>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#2c5aa0',
                marginBottom: '1rem',
              }}
            >
              Service Availability
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginBottom: '1rem',
                textAlign: 'justify',
              }}
            >
              We strive to provide reliable service, but we do not guarantee uninterrupted or error-free operation. The service may be temporarily unavailable due to maintenance, updates, or unforeseen circumstances. We are not liable for any loss of data or service interruption.
            </Typography>
          </Box>

          <Box sx={{ marginBottom: '2rem' }}>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#2c5aa0',
                marginBottom: '1rem',
              }}
            >
              Limitation of Liability
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginBottom: '1rem',
                textAlign: 'justify',
              }}
            >
              To the maximum extent permitted by law, we disclaim all warranties and shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from your use of the service.
            </Typography>
          </Box>

          <Box sx={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid #e0e0e0' }}>
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                textAlign: 'center',
              }}
            >
              <Link
                to="/"
                style={{
                  color: '#2c5aa0',
                  textDecoration: 'none',
                }}
              >
                ← Back to Home
              </Link>
              {' | '}
              <Link
                to="/privacy-policy"
                style={{
                  color: '#2c5aa0',
                  textDecoration: 'none',
                }}
              >
                Privacy Policy
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default TermsOfServicePage;

